import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../api/axios";
import "./DesviosEditarPage.css";

/* Modais */
import CriarDesvioEditarModal from "./popups/CriarDesvioEditarModal.jsx";
import EditarDesvioEditarModal from "./popups/EditarDesvioEditarModal.jsx";
import AtivarDesvioEditarModal from "./popups/AtivarDesvioEditarModal.jsx";
import InativarDesvioEditarModal from "./popups/InativarDesvioEditarModal.jsx";
import ExcluirDesvioEditarModal from "./popups/ExcluirDesvioEditarModal.jsx";

/* helpers */
const UP = (v) => (typeof v === "string" ? v.toUpperCase() : v ?? "");

function IconEdit(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}
function IconTrash(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`dve-toggle ${on ? "on" : "off"}`}
      onClick={onToggle}
      title={title || (on ? "Inativar" : "Ativar")}
    >
      <span className="knob" />
    </button>
  );
}

/* ===== API (fallbacks) ===== */
async function apiList(params) {
  try {
    return await api.get("/desvios_editar", { params });
  } catch {
    return await api.get("/desvios_editar/listar", { params });
  }
}
async function apiCreate(body) {
  try {
    return await api.post("/desvios_editar", body);
  } catch {
    return await api.post("/desvios_editar/cadastrar", body);
  }
}
async function apiUpdate(id, body) {
  try {
    return await api.put(`/desvios_editar/${id}`, body);
  } catch {
    return await api.put(`/desvios_editar/editar/${id}`, body);
  }
}
async function apiToggle(id, ativo) {
  try {
    return await api.patch(`/desvios_editar/${id}/ativo`, { ativo });
  } catch {
    return await api.patch(`/desvios_editar/alterar-ativo/${id}`, { ativo });
  }
}
async function apiRemove(id) {
  try {
    return await api.delete(`/desvios_editar/${id}`);
  } catch {
    return await api.delete(`/desvios_editar/excluir/${id}`);
  }
}

function sortRows(a, b) {
  const oa = Number(a?.ordem ?? 0);
  const ob = Number(b?.ordem ?? 0);
  if (oa !== ob) return oa - ob;

  const ca = String(a?.causador ?? "").localeCompare(String(b?.causador ?? ""), "pt-BR", {
    sensitivity: "base",
  });
  if (ca !== 0) return ca;

  const co = String(a?.codigo ?? "").localeCompare(String(b?.codigo ?? ""), "pt-BR", {
    sensitivity: "base",
  });
  if (co !== 0) return co;

  return Number(a?.id ?? 0) - Number(b?.id ?? 0);
}

export default function DesviosEditarPage({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [grupoFiltro, setGrupoFiltro] = useState(null);

  const [openCreate, setOpenCreate] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const [confirm, setConfirm] = useState({
    open: false,
    kind: null, // "ativar" | "inativar" | "excluir"
    payload: null,
  });

  useEffect(() => {
    document.body.classList.add("dved-skin");
    return () => document.body.classList.remove("dved-skin");
  }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const ativos = rows.filter((r) => Number(r.ativo)).length;
    const inativos = total - ativos;

    const porGrupo = {};
    for (const r of rows) {
      const g = UP(r?.causador || "-");
      porGrupo[g] = (porGrupo[g] || 0) + 1;
    }
    const chips = Object.entries(porGrupo).sort((a, b) =>
      a[0].localeCompare(b[0], "pt-BR", { sensitivity: "base" })
    );

    return { total, ativos, inativos, chips };
  }, [rows]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = {
        limit: 1000,
        offset: 0,
      };
      const r = await apiList(params);
      const data = r?.data?.data ?? r?.data ?? [];
      setRows(Array.isArray(data) ? [...data].sort(sortRows) : []);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Erro ao listar");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const visiveis = useMemo(() => {
    if (!grupoFiltro) return rows;
    return rows.filter((r) => UP(r?.causador || "-") === UP(grupoFiltro));
  }, [rows, grupoFiltro]);

  function goHome() {
    if (onBack) onBack();
    else if (window.history.length > 1) window.history.back();
    else window.location.href = "/";
  }

  function renderColGroup() {
    return (
      <colgroup>
        <col style={{ width: "24%" }} />
        <col style={{ width: "58%" }} />
        <col style={{ width: "18%" }} />
      </colgroup>
    );
  }

  function askToggle(row) {
    const isOn = Number(row.ativo) === 1;
    setConfirm({
      open: true,
      kind: isOn ? "inativar" : "ativar",
      payload: { id: row.id, label: row.causador || row.codigo || `#${row.id}` },
    });
  }

  function askDelete(row) {
    setConfirm({
      open: true,
      kind: "excluir",
      payload: { id: row.id, label: row.causador || row.codigo || `#${row.id}` },
    });
  }

  function microUpsert(updated) {
    setRows((prev) => {
      const next = [...prev];
      const i = next.findIndex((x) => Number(x.id) === Number(updated.id));
      if (i >= 0) next[i] = { ...next[i], ...updated };
      else next.push(updated);
      next.sort(sortRows);
      return next;
    });
  }

  function microRemove(id) {
    setRows((prev) => prev.filter((x) => Number(x.id) !== Number(id)));
  }

  async function handleConfirm() {
    const { kind, payload } = confirm || {};
    if (!kind || !payload?.id) return;

    try {
      if (kind === "ativar") {
        await apiToggle(payload.id, 1);
        microUpsert({ id: payload.id, ativo: 1 });
      }
      if (kind === "inativar") {
        await apiToggle(payload.id, 0);
        microUpsert({ id: payload.id, ativo: 0 });
      }
      if (kind === "excluir") {
        await apiRemove(payload.id);
        microRemove(payload.id);
      }
      setConfirm({ open: false, kind: null, payload: null });
    } catch (e) {
      alert(e?.response?.data?.error || e.message || "Erro ao executar ação");
    }
  }

  // ✅ CÓDIGO automático (backend):
  // - Create: NÃO envia codigo
  // - Edit:   NÃO envia codigo
  async function handleCreateSubmit(form) {
    try {
      const body = {
        causador: UP(form.causador || ""),
        descricao: String(form.descricao || "").trim() || null,
        ordem: Number.isFinite(Number(form.ordem)) ? Number(form.ordem) : 0,
        ativo: Number(form.ativo) ? 1 : 0,
      };
      if (!body.causador) return alert("Preencha o campo: Causador.");

      const { data } = await apiCreate(body);
      const r = data?.data ?? data;
      if (r?.id) microUpsert(r);
      else await carregar();

      setOpenCreate(false);
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao cadastrar");
    }
  }

  async function handleEditSubmit(form) {
    try {
      const id = form?.id;
      if (!id) return;

      const body = {
        causador: UP(form.causador || ""),
        descricao: String(form.descricao || "").trim() || null,
        ordem: Number.isFinite(Number(form.ordem)) ? Number(form.ordem) : 0,
        ativo: Number(form.ativo) ? 1 : 0,
      };
      if (!body.causador) return alert("Preencha o campo: Causador.");

      const { data } = await apiUpdate(id, body);
      const r = data?.data ?? data;
      if (r?.id) microUpsert(r);
      else await carregar();

      setEditRow(null);
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao editar");
    }
  }

  return (
    <div className="dve-shell">
      {/* TOPBAR */}
      <div className="dve-topbar">
        <button className="dve-btn-hero left" type="button" onClick={goHome}>
          Voltar para o início
        </button>

        <h1 className="dve-title">Catálago de Desvios — Edição</h1>

        <button className="dve-btn-hero right" type="button" onClick={() => setOpenCreate(true)}>
          Cadastrar
        </button>
      </div>

      {/* METRICAS + POR GRUPO */}
      <div className="dve-head">
        <div className="dve-cards-row dve-cards--all">
          <div className="dve-card">
            <div className="dve-card__label">TOTAL</div>
            <div className="dve-card__value">{stats.total}</div>
          </div>
          <div className="dve-card dve-card--ok">
            <div className="dve-card__label">ATIVOS</div>
            <div className="dve-card__value">{stats.ativos}</div>
          </div>
          <div className="dve-card dve-card--alert">
            <div className="dve-card__label">INATIVOS</div>
            <div className="dve-card__value">{stats.inativos}</div>
          </div>

          <div className="dve-chips-panel">
            <div className="dve-chips-panel__label">POR GRUPO</div>
            <div className="dve-chip-list">
              {stats.chips.map(([g, q]) => (
                <button
                  key={g}
                  className={`dve-chip ${grupoFiltro && UP(grupoFiltro) === UP(g) ? "active" : ""}`}
                  type="button"
                  onClick={() => setGrupoFiltro((prev) => (prev && UP(prev) === UP(g) ? null : g))}
                  title={`Filtrar por ${g}`}
                >
                  {g}
                  <span className="dve-chip__num">{q}</span>
                </button>
              ))}
              {grupoFiltro && (
                <button className="dve-chip dve-chip--clear" type="button" onClick={() => setGrupoFiltro(null)} title="Limpar filtro">
                  Limpar filtro x
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* LISTAGEM */}
      <div className="dve-wrap">
        {loading ? (
          <div className="dve-empty">Carregando...</div>
        ) : err ? (
          <div className="dve-empty">{err}</div>
        ) : visiveis.length === 0 ? (
          <div className="dve-empty">Nenhum item encontrado.</div>
        ) : (
          <div className="dve-table-wrap">
            <table className="dve-table dve-table--head">
              {renderColGroup()}
              <thead>
                <tr>
                  <th>CAUSADOR</th>
                  <th>DESCRICAO</th>
                  <th>CONTROLES</th>
                </tr>
              </thead>
            </table>

            <div className="dve-table-body-scroll">
              <table className="dve-table dve-table--body">
                {renderColGroup()}
                <tbody>
                  {visiveis.map((row) => (
                    <tr key={row.id}>
                      <td className="dve-td dve-td-strong">{UP(row.causador || "-")}</td>
                      <td className="dve-td dve-td-left">{row.descricao || "-"}</td>
                      <td className="dve-td dve-td-controls">
                        <div className="dve-actions dve-actions--cluster">
                          <ToggleSwitch
                            on={!!Number(row.ativo)}
                            onToggle={() => askToggle(row)}
                            title={Number(row.ativo) ? "Inativar" : "Ativar"}
                          />
                          <span className="dve-actions-divider" aria-hidden="true" />
                          <button className="dve-icon-btn" title="Editar" onClick={() => setEditRow(row)}>
                            <IconEdit />
                          </button>
                          <span className="dve-actions-divider" aria-hidden="true" />
                          <button className="dve-icon-btn" title="Excluir" onClick={() => askDelete(row)}>
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

            {/* MODAIS */}
      {openCreate && (
        <CriarDesvioEditarModal
          open
          initial={{
            causador: "",
            descricao: "",
            ordem: 0,
            ativo: "",
          }}
          onClose={() => setOpenCreate(false)}
          onSubmit={handleCreateSubmit}
        />
      )}

      {editRow && (
        <EditarDesvioEditarModal
          open
          initial={{
            id: editRow.id,
            codigo: editRow.codigo ?? "",
            causador: editRow.causador ?? "",
            descricao: editRow.descricao ?? "",
            ordem: Number(editRow.ordem ?? 0),
            ativo: Number(editRow.ativo) ? 1 : 0,
          }}
          onClose={() => setEditRow(null)}
          onSubmit={handleEditSubmit}
        />
      )}

      {confirm.open && confirm.kind === "ativar" && (
        <AtivarDesvioEditarModal
          open
          label={UP(confirm.payload?.label)}
          onClose={() => setConfirm({ open: false, kind: null, payload: null })}
          onConfirm={handleConfirm}
        />
      )}

      {confirm.open && confirm.kind === "inativar" && (
        <InativarDesvioEditarModal
          open
          label={UP(confirm.payload?.label)}
          onClose={() => setConfirm({ open: false, kind: null, payload: null })}
          onConfirm={handleConfirm}
        />
      )}

      {confirm.open && confirm.kind === "excluir" && (
        <ExcluirDesvioEditarModal
          open
          label={UP(confirm.payload?.label)}
          onClose={() => setConfirm({ open: false, kind: null, payload: null })}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
